import { getMediaClient as getAkMediaClient } from '@atlaskit/media-client-react';
import { type MediaClientConfig } from '@atlaskit/media-core';
import { type AsapClaims } from '@atlassian/asap-interface';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { collabStateManager } from '@databases/collab-state-manager';
// eslint-disable-next-line workspaces/require-dependency
import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { logWarning } from '@databases/logs';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import jwtDecode from 'jwt-decode';

import { type CommonRootState } from './redux/redux';

export type MediaClient = ReturnType<typeof getAkMediaClient>;

// AtlasKit's getMediaClient stores an internal Map<MediaClientConfig, MediaClient>.
// We map each CollabManager to a MediaClient by mapping it to a config object here
// and calling getMediaClient to map that to a MediaClient.
const mediaClientConfig = new Map<CollabManager, MediaClientConfig>();

export function getMediaClient(
  collabManager: CollabManager,
  enforceDataSecurityPolicy?: boolean
): MediaClient {
  let config = mediaClientConfig.get(collabManager);
  if (!config) {
    // Default to true, if DSP evaluation cannot be provided
    const shouldEnforceDSP =
      enforceDataSecurityPolicy !== undefined ? enforceDataSecurityPolicy : true;
    config = {
      authProvider: async () => {
        const token = await collabManager.tokenManager.token();
        return {
          clientId: jwtDecode<AsapClaims>(token.mediaToken).iss,
          token: token.mediaToken,
          baseUrl: token.mediaBaseUrl,
        };
      },
      useSha256ForUploads: true,
      enforceDataSecurityPolicy: shouldEnforceDSP,
    };
    mediaClientConfig.set(collabManager, config);
  }

  return getAkMediaClient(config);
}

export async function getMediaCollectionId(
  state: CommonRootState,
  contentId: string
): Promise<string | undefined> {
  const collabManager = collabStateManager.getCollabManager(contentId);
  if (!collabManager) {
    return undefined;
  }

  try {
    const token = await collabManager.tokenManager.token();
    return `contentId-${token.mediaCollectionId}`;
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (error: unknown) {
    if (error instanceof Error) {
      logWarning('Failed to get token for media collection ID', error);
    }
    return;
  }
}

export async function getImageUrl(
  state: CommonRootState,
  contentId: string,
  fileId: string
) {
  const collabManager = collabStateManager.getCollabManager(contentId);
  const token = collabManager && (await collabManager.tokenManager.token());
  // Default to true, if DSP evaluation cannot be provided
  const enforceDataSecurityPolicy =
    token?.dataSecurityPolicy?.attachmentDownload !== true;
  const mediaClient =
    collabManager && getMediaClient(collabManager, enforceDataSecurityPolicy);

  const mediaCollectionId = await getMediaCollectionId(state, contentId);
  if (mediaCollectionId && mediaClient) {
    return await mediaClient.getImageUrl(fileId, {
      collection: mediaCollectionId,
    });
  }
}
